<template>
  <div class="mt-6">
    <!-- Loading State -->
    <div v-if="aiSummaryLoading" class="flex flex-col items-center mt-4">
      <span class="text-black text-lg">
        Retrieving response, this may take a moment
        <span class="dots-animation">
          <span class="dot">.</span>
          <span class="dot">.</span>
          <span class="dot">.</span>
        </span>
      </span>
    </div>

    <!-- AI Insights Content -->
    <VueLoading :active.sync="aiSummaryLoading" :is-full-page="false" />

    <div v-if="aiInsights && !aiSummaryLoading" class="bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg p-6 border border-gray-200">
      <!-- Header -->
      <div class="flex items-center mb-4">
        <div class="w-9 h-9 bg-transparent rounded-lg flex items-center justify-center mr-3">
          <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path>
          </svg>
        </div>
        <h3 class="text-lg font-semibold text-white">{{ aiInsights.title }}</h3>
      </div>

      <!-- Summary Text -->
      <p class="text-white mb-6 leading-relaxed">{{ aiInsights.summary }}</p>

      <!-- Metrics Grid -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div
          v-for="(metric, index) in aiInsights.metrics"
          :key="index"
          :class="[
            'rounded-lg p-4 border border-gray-100 shadow-sm',
            index === 0 ? 'bg-gradient-to-r from-blue-500 to-blue-600' : '',
            index === 1 ? 'bg-gradient-to-r from-blue-600 to-purple-500' : '',
            index === 2 ? 'bg-gradient-to-r from-purple-500 to-purple-600' : ''
          ]"
        >
          <div class="flex items-start">
            <div class="w-10 h-10 bg-transparent rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
              <!-- Dynamic icon based on metric type -->
              <svg v-if="metric.title.includes('Cost')" class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"></path>
              </svg>
              <svg v-else-if="metric.title.includes('Deployment')" class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
              </svg>
              <svg v-else-if="metric.title.includes('Reliability') || metric.title.includes('System')" class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <svg v-else class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
              </svg>
            </div>
            <div class="flex-1">
              <h4 class="font-medium text-white text-sm">{{ metric.title }}</h4>
              <p class="font-semibold text-white mt-1">{{ metric.value }}</p>
              <p class="text-xs text-gray-100 mt-1">{{ metric.description }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="flex flex-col sm:flex-row gap-3 mb-6">
        <button
          v-if="zoomEnabled"
          class="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
          @click="$emit('investigate-points')"
        >
          {{ flowEvents.length > 0 ? 'Refresh Detailed Analysis' : 'View Detailed Analysis' }}
        </button>
      </div>

      <!-- Detailed Flow Events Display -->
      <div v-if="(flowEvents && flowEvents.length > 0) || detailedAnalysisLoading" class="bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg p-6 mt-6">
        <!-- Detailed Analysis Loading State -->
        <div v-if="detailedAnalysisLoading" class="flex flex-col items-center mt-4">
          <span class="text-white text-lg">
            Retrieving detailed response, this may take a moment
            <span class="dots-animation">
              <span class="dot">.</span>
              <span class="dot">.</span>
              <span class="dot">.</span>
            </span>
          </span>
        </div>

        <!-- Detailed Analysis Content -->
        <div v-if="!detailedAnalysisLoading">
          <div class="flex items-center mb-6">
            <div class="w-6 h-6 bg-transparent rounded-lg flex items-center justify-center mr-3">
              <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
              </svg>
            </div>
            <h4 class="text-md font-semibold text-white">Detailed Event Analysis</h4>
          </div>

          <!-- Event Timeline -->
          <div class="space-y-4 mb-6">
            <div
              v-for="(event, index) in flowEvents"
              :key="event.id"
              class="relative flex items-start pb-4"
              :class="{ 'border-b border-gray-100': index < flowEvents.length - 1 }"
            >
              <!-- Event Icon -->
              <div class="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center mr-4 bg-transparent">
                <!-- Deployment Icon -->
                <svg v-if="event.type === 'deployment'" class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                </svg>
                <!-- Alert Icon -->
                <svg v-else-if="event.type === 'alert'" class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.864-.833-2.634 0L4.18 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                </svg>
                <!-- Cost Icon -->
                <svg v-else-if="event.type === 'cost'" class="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"></path>
                </svg>
                <!-- Default Icon -->
                <svg v-else class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
              </div>

              <!-- Event Content -->
              <div class="flex-1 min-w-0">
                <div class="flex items-center justify-between mb-2">
                  <h5 class="text-sm font-semibold truncate px-2 py-1 rounded"
                    :class="{
                      'text-green-600 bg-green-100': event.type === 'deployment',
                      'text-red-600 bg-red-100': event.type === 'alert',
                      'text-blue-600 bg-blue-100': event.type === 'cost',
                      'text-gray-600 bg-gray-100': !['deployment', 'alert', 'cost'].includes(event.type)
                    }">{{ event.label }}</h5>
                  <span class="text-xs text-gray-500 font-medium px-2 py-1 bg-gray-100 rounded-full">
                    {{ event.date }}
                  </span>
                </div>
                <p v-if="event.description" class="text-sm text-white leading-relaxed">
                  {{ event.description }}
                </p>
                <!-- Event Type Badge -->
                <span class="inline-flex items-center px-2 py-1 mt-2 text-xs font-medium rounded-full"
                      :class="{
                        'bg-green-100 text-green-800': event.type === 'deployment',
                        'bg-red-100 text-red-800': event.type === 'alert',
                        'bg-blue-100 text-blue-800': event.type === 'cost',
                        'bg-gray-100 text-gray-800': !['deployment', 'alert', 'cost'].includes(event.type)
                      }">
                  {{ event.type.charAt(0).toUpperCase() + event.type.slice(1) }}
                </span>
              </div>
            </div>
          </div>

          <!-- Event Summary -->
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t border-gray-100">
            <div class="text-center">
              <div class="text-3xl font-bold text-green-600 bg-green-100 rounded-lg p-3 max-w-xs mx-auto">
                {{ flowEvents.filter(e => e.type === 'deployment').length }}
                <div class="text-lg">Deployments</div>
              </div>
            </div>
            <div class="text-center">
              <div class="text-3xl font-bold text-red-600 bg-red-100 rounded-lg p-3 max-w-xs mx-auto">
                {{ flowEvents.filter(e => e.type === 'alert').length }}
                <div class="text-lg">Alerts</div>
              </div>
            </div>
            <div class="text-center">
              <div class="text-3xl font-bold text-blue-600 bg-blue-100 rounded-lg p-3 max-w-xs mx-auto">
                {{ flowEvents.filter(e => e.type === 'cost').length }}
                <div class="text-lg">Cost Events</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Error State -->
    <div v-if="aiInsightsError && !aiSummaryLoading" class="bg-red-50 rounded-lg p-4 border border-red-200">
      <div class="flex items-center mb-2">
        <svg class="w-5 h-5 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        <h3 class="text-sm font-medium text-red-800">Analysis Error</h3>
      </div>
      <p class="text-sm text-red-700">{{ aiInsightsError }}</p>
      <button
        class="mt-3 px-3 py-1 bg-red-100 text-red-800 rounded text-sm hover:bg-red-200 transition-colors"
        @click="$emit('retry-analysis')"
      >
        Retry Analysis
      </button>
    </div>

    <!-- Fallback for simple text summary -->
    <div v-if="aiSummary && !aiSummaryLoading && !aiInsights" class="bg-gray-50 rounded-lg p-4 border border-gray-200">
      <h3 class="text-lg font-medium text-gray-700 mb-2">AI-Generated Summary</h3>
      <p class="text-sm text-gray-900">{{ aiSummary }}</p>
      <button
        v-if="zoomEnabled"
        class="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
        @click="$emit('investigate-points')"
      >
        Investigate
      </button>
    </div>
  </div>
</template>

<script>
import VueLoading from 'vue-loading-overlay'

export default {
  name: 'CombinedMetricsAnalysis',
  components: {
    VueLoading
  },
  props: {
    aiInsights: {
      type: Object,
      default: null
    },
    aiSummary: {
      type: String,
      default: ''
    },
    aiInsightsError: {
      type: String,
      default: null
    },
    aiSummaryLoading: {
      type: Boolean,
      default: false
    },
    detailedAnalysisLoading: {
      type: Boolean,
      default: false
    },
    flowEvents: {
      type: Array,
      default: () => []
    },
    zoomEnabled: {
      type: Boolean,
      default: false
    }
  }
}
</script>

<style scoped>
.dots-animation {
  display: inline-block;
}

.dot {
  opacity: 0;
  animation: dots 1.5s infinite;
}

.dot:nth-child(1) {
  animation-delay: 0s;
}

.dot:nth-child(2) {
  animation-delay: 0.5s;
}

.dot:nth-child(3) {
  animation-delay: 1s;
}

@keyframes dots {
  0%, 20% {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Cards/AICards/TimeSeriesCard/CombinedMetrics/CombinedMetricsAnalysis.vue